import React from 'react';
import {
  Alert,
  App,
  Button,
  Card,
  Col,
  Flex,
  Row,
  Space,
  Tag,
  Typography,
  theme,
} from 'antd';
import { CheckCircleTwoTone } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useSubscriptionCheckout, useSubscriptionPortal } from '../../../libs/ohat-api';
import { usePlan } from '../../../hooks/usePlan';
import { useAuth } from '../../../hooks/useAuth';
import { Loading } from '../../components/common/Loading';
import { PlanPageBreadCrumb } from '../../components/breadcrumb/PlanPageBreadCrumb';
import { UsageBar } from '../../components/common/UsageBar';
import { formatYYYYMMDDSlash } from '../../../utils/date';
import { getApiErrorMessage } from '../../../utils/apiError';

/**
 * プランの機能一覧。
 * antd の List は v6 で非推奨（次のメジャーで削除）のため、
 * 従来の `List size="small"` 相当の見た目を素の要素で再現している。
 */
const FeatureList: React.FC<{ features: string[] }> = ({ features }) => {
  const { token } = theme.useToken();
  return (
    <Flex vertical>
      {features.map((feature, index) => (
        <Space
          key={feature}
          style={{
            padding: `${token.paddingContentVerticalSM}px ${token.paddingContentHorizontal}px`,
            borderBlockEnd:
              index === features.length - 1
                ? undefined
                : `1px solid ${token.colorSplit}`,
          }}
        >
          <CheckCircleTwoTone twoToneColor="#52c41a" />
          {feature}
        </Space>
      ))}
    </Flex>
  );
};

export const PlanPage: React.FC = () => {
  const { t } = useTranslation();

  // 機能リストはコンポーネント内でt()を使って生成する
  const FREE_FEATURES = [
    t('plan.features.record'),
    t('plan.features.chart'),
    t('plan.features.pdf'),
    t('plan.features.archive'),
    t('plan.features.search'),
    t('plan.features.photo10'),
  ];

  const LIGHT_FEATURES = [
    t('plan.features.allFree'),
    t('plan.features.tag'),
    t('plan.features.csv'),
    t('plan.features.memo'),
    t('plan.features.care'),
    t('plan.features.multiLanguage'),
    t('plan.features.photo100'),
    t('plan.features.scoreFilter'),
  ];

  const { message } = App.useApp();
  const { isOwner } = useAuth();
  const { planInfo, isLoading, isFree, isLight, getUsage } = usePlan();
  const checkout = useSubscriptionCheckout();
  const portal = useSubscriptionPortal();
  const photoUsage = getUsage('max_photos_per_month');

  const isPastDue = planInfo?.stripeStatus === 'past_due';
  // 決済が完了していない状態（Stripe 上に契約はあるが支払い未了）。
  // planKey は free だが新規申し込みはできないため、申し込みではなく Portal へ誘導する。
  const isPaymentIncomplete =
    planInfo?.stripeStatus === 'unpaid' ||
    planInfo?.stripeStatus === 'incomplete';
  // API 側で新規 Checkout が拒否される状態か。planKey が free でも true になり得る
  // （unpaid / incomplete）ため、ボタンの出し分けは planKey ではなくこれを見る。
  const billingActive = planInfo?.billingActive === true;
  // 申し込み（Checkout）を出せるのは、ライトでもなく Stripe 上に契約もない場合だけ。
  // それ以外は「プラン・お支払い情報の管理」（Portal）へ誘導する。
  const canCheckout = !isLight && !billingActive;
  // トライアル中は planKey は light だが課金はまだ始まっていない。
  // 無料期間の終わり（= 初回請求日）は currentPeriodEnd に入る。
  const isTrialing = planInfo?.stripeStatus === 'trialing';
  // 無料トライアルは1事業所につき1回だけ。2回目以降の契約では訴求を出さない。
  // 取得前（undefined）は誤って「無料」と見せないよう false 扱いにする。
  const trialAvailable = planInfo?.trialAvailable === true;
  const owner = isOwner();

  // Stripeが返すURLへリダイレクトする共通処理（checkout/portal共通）。
  const redirectVia = async (
    mutateAsync: () => Promise<{ url?: string } | undefined>,
    failMsg: string,
  ) => {
    try {
      const res = await mutateAsync();
      if (res?.url) {
        window.location.href = res.url;
      } else {
        message.error(failMsg);
      }
    } catch (e) {
      // 409（契約済み）や 404（お支払い情報なし）はAPIが具体的な案内を返すため、
      // 汎用メッセージで上書きせずそのまま表示する。
      message.error(getApiErrorMessage(e, failMsg));
    }
  };

  const handleCheckout = () =>
    redirectVia(() => checkout.mutateAsync(), t('plan.checkoutError'));

  const handlePortal = () =>
    redirectVia(() => portal.mutateAsync(), t('plan.portalError'));

  if (isLoading) return <Loading />;

  return (
    <div style={{ width: '100%', maxWidth: 960 }}>
      <div style={{ marginBottom: 16 }}>
        <PlanPageBreadCrumb />
      </div>
      <Typography.Title level={3}>{t('plan.title')}</Typography.Title>

      {isPastDue && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          title={t('plan.paymentIssueTitle')}
          description={t('plan.paymentIssueDesc')}
        />
      )}

      {isPaymentIncomplete && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          title={t('plan.paymentIncompleteTitle')}
          description={t('plan.paymentIncompleteDesc')}
        />
      )}

      {!owner && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          title={t('plan.ownerOnly')}
        />
      )}

      <Card title={t('plan.usageTitle')} style={{ marginBottom: 16 }}>
        {photoUsage ? (
          <UsageBar
            current={photoUsage.current}
            limit={photoUsage.limit}
            label={t('plan.usagePhoto')}
          />
        ) : (
          <Typography.Text type="secondary">
            {t('plan.usageLoading')}
          </Typography.Text>
        )}
      </Card>

      <Row gutter={[16, 16]}>
        <Col xs={24} md={12}>
          <Card
            title={t('plan.free')}
            extra={isFree ? <Tag color="blue">{t('plan.current')}</Tag> : null}
          >
            <Typography.Paragraph strong style={{ fontSize: 20 }}>
              ¥0<Typography.Text type="secondary">{t('plan.perMonth')}</Typography.Text>
            </Typography.Paragraph>
            <FeatureList features={FREE_FEATURES} />
          </Card>
        </Col>

        <Col xs={24} md={12}>
          <Card
            title={t('plan.light')}
            extra={
              isLight ? (
                <Tag color={isTrialing ? 'gold' : 'green'}>
                  {isTrialing ? t('plan.trialing') : t('plan.current')}
                </Tag>
              ) : null
            }
          >
            <Typography.Paragraph strong style={{ fontSize: 20, marginBottom: 8 }}>
              ¥5,000
              <Typography.Text type="secondary">{t('plan.perMonthLight')}</Typography.Text>
            </Typography.Paragraph>

            {canCheckout && trialAvailable && (
              <Alert
                type="success"
                showIcon
                style={{ marginBottom: 12 }}
                title={t('plan.trialBadge')}
                description={t('plan.trialLead')}
              />
            )}

            {isTrialing && planInfo?.currentPeriodEnd && (
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 12 }}
                title={t('plan.trialUntil', {
                  date: formatYYYYMMDDSlash(planInfo.currentPeriodEnd),
                })}
              />
            )}

            <FeatureList features={LIGHT_FEATURES} />
            <div style={{ marginTop: 16 }}>
              {canCheckout ? (
                <>
                  <Button
                    type="primary"
                    block
                    disabled={!owner}
                    loading={checkout.isPending}
                    onClick={handleCheckout}
                  >
                    {trialAvailable
                      ? t('plan.upgrade')
                      : t('plan.upgradeNoTrial')}
                  </Button>
                  <Typography.Text
                    type="secondary"
                    style={{ fontSize: 12, display: 'block', marginTop: 8 }}
                  >
                    {trialAvailable
                      ? t('plan.trialNote')
                      : t('plan.trialUsedNote')}
                  </Typography.Text>
                </>
              ) : (
                <>
                  <Button
                    block
                    disabled={!owner}
                    loading={portal.isPending}
                    onClick={handlePortal}
                  >
                    {t('plan.manage')}
                  </Button>
                  <Typography.Text
                    type="secondary"
                    style={{ fontSize: 12, display: 'block', marginTop: 8 }}
                  >
                    {t('plan.manageDesc')}
                  </Typography.Text>
                </>
              )}
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};
